import { useState, useRef } from "react";
import { X } from "lucide-react";
import type { CandidateProfile } from "@applicant-reviewer/api";
import { useUpdateProfile } from "../../../../hooks/mutations/useProfileMutations";

interface WorkPreferencesSectionProps {
  profile: CandidateProfile;
}

interface FormState {
  preferredRoles: string[];
  preferredLocations: string[];
  certifications: string[];
}

interface TagFieldProps {
  label: string;
  tags: string[];
  onAdd: (value: string) => void;
  onRemove: (index: number) => void;
  placeholder: string;
}

function TagField({ label, tags, onAdd, onRemove, placeholder }: TagFieldProps) {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (trimmed && !tags.includes(trimmed)) {
        onAdd(trimmed);
      }
      setInputValue("");
    }
  };

  const handleBlurAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !tags.includes(trimmed)) {
      onAdd(trimmed);
      setInputValue("");
    }
  };

  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-2">{label}</label>

      {/* Tag list */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {tags.map((tag, index) => (
            <span
              key={`${tag}-${index}`}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-50 text-[#0156FF] text-xs font-semibold"
            >
              {tag}
              <button
                type="button"
                onClick={() => onRemove(index)}
                className="ml-0.5 hover:text-blue-800 transition-colors"
                aria-label={`Remove ${tag}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input */}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlurAdd}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0156FF] focus:border-transparent transition"
      />
      <p className="text-xs text-gray-400 mt-1">Press Enter to add</p>
    </div>
  );
}

export default function WorkPreferencesSection({ profile }: WorkPreferencesSectionProps) {
  const [form, setForm] = useState<FormState>({
    preferredRoles: profile.preferredRoles ?? [],
    preferredLocations: profile.preferredLocations ?? [],
    certifications: profile.certifications ?? [],
  });
  const [saved, setSaved] = useState(false);
  const updateProfile = useUpdateProfile();

  const addTag = (field: keyof FormState) => (value: string) => {
    setSaved(false);
    setForm((prev) => ({ ...prev, [field]: [...prev[field], value] }));
  };

  const removeTag = (field: keyof FormState) => (index: number) => {
    setSaved(false);
    setForm((prev) => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
  };

  const handleSave = () => {
    setSaved(false);
    updateProfile.mutate(
      {
        preferredRoles: form.preferredRoles.length > 0 ? form.preferredRoles : undefined,
        preferredLocations: form.preferredLocations.length > 0 ? form.preferredLocations : undefined,
        certifications: form.certifications.length > 0 ? form.certifications : undefined,
      },
      { onSuccess: () => setSaved(true) },
    );
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-5">
      <h2 className="text-base font-bold text-gray-900">Work Preferences</h2>

      <TagField
        label="Preferred Roles"
        tags={form.preferredRoles}
        onAdd={addTag("preferredRoles")}
        onRemove={removeTag("preferredRoles")}
        placeholder="e.g. Cashier, Barista"
      />

      <TagField
        label="Preferred Locations"
        tags={form.preferredLocations}
        onAdd={addTag("preferredLocations")}
        onRemove={removeTag("preferredLocations")}
        placeholder="e.g. Downtown, Eastside"
      />

      <TagField
        label="Certifications"
        tags={form.certifications}
        onAdd={addTag("certifications")}
        onRemove={removeTag("certifications")}
        placeholder="e.g. Food Handler, CPR"
      />

      {updateProfile.isError && (
        <p className="text-xs text-red-600">Save failed. Please try again.</p>
      )}

      {saved && !updateProfile.isPending && (
        <p className="text-xs text-green-600 font-semibold">Saved!</p>
      )}

      <button
        type="button"
        onClick={handleSave}
        disabled={updateProfile.isPending}
        className="w-full h-12 rounded-full bg-[#0156FF] text-white text-sm font-bold hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {updateProfile.isPending ? "Saving..." : "Save Preferences"}
      </button>
    </div>
  );
}
